import React, { useState, useMemo, useCallback } from 'react';
import EmptyState from '../components/EmptyState';
import './StyleTab.css';

const PositionsTab = ({ 
  positions = [], 
  positionCategories = [],
  departments = [],
  isLoading = false,
  onDataChange 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [newPosition, setNewPosition] = useState({ 
    name: '', 
    category_id: positionCategories[0]?.id || 1, 
    category_name: positionCategories[0]?.name || '' 
  });
  const [editingPosition, setEditingPosition] = useState(null);
  const [searchPosition, setSearchPosition] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedDepartments, setExpandedDepartments] = useState({});

  // Показ уведомления
  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  }, []);

  // Обновляем newPosition при загрузке категорий
  React.useEffect(() => {
    if (positionCategories.length > 0 && !newPosition.category_name) {
      setNewPosition(prev => ({
        ...prev,
        category_id: positionCategories[0]?.id,
        category_name: positionCategories[0]?.name
      }));
    }
  }, [positionCategories, newPosition.category_name]);

  // Сброс формы добавления
  const resetForm = () => {
    setNewPosition({ 
      name: '', 
      category_id: positionCategories[0]?.id || 1, 
      category_name: positionCategories[0]?.name || '' 
    });
  };

  // Переключение группировки по подразделению
  const toggleDepartment = useCallback((departmentId) => {
    setExpandedDepartments(prev => ({
      ...prev,
      [departmentId]: !prev[departmentId]
    }));
  }, []);

  // Открытие модального окна редактирования
  const openEditModal = useCallback((position) => {
    setEditingPosition({
      id: position.id,
      name: position.name,
      category_id: position.category_id,
      category_name: position.category_name || '',
      department_id: position.currentDepartment?.id || position.departments?.[0]?.id || ''
    });
    setIsEditModalOpen(true);
  }, []);

  // Закрытие модального окна редактирования
  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingPosition(null);
  }, []);

  const addPosition = useCallback(async () => {
    if (!newPosition.name.trim()) {
      showToast('Введите название должности', 'error');
      return;
    }
    const selectedCategory = positionCategories.find(cat => cat.id === newPosition.category_id);
    setLoading(true);
    try {
      const { positionService } = await import('../../../services/positionService');
      await positionService.create({
        name: newPosition.name.trim(),
        category_id: newPosition.category_id,
        category_name: selectedCategory?.name || ''
      });
      
      // Инвалидируем кеш и обновляем данные
      if (onDataChange) {
        await onDataChange();
      }
      
      resetForm();
      setIsModalOpen(false);
      showToast('Должность успешно добавлена', 'success');
    } catch (err) {
      showToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [newPosition, positionCategories, onDataChange, showToast]);

  const updatePosition = useCallback(async () => {
    if (!editingPosition.name.trim()) {
      showToast('Введите название должности', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const { positionService } = await import('../../../services/positionService');
      
      const updateData = {
        name: editingPosition.name.trim(),
        category_id: editingPosition.category_id,
        category_name: editingPosition.category_name
      };
      
      if (editingPosition.department_id) {
        updateData.department_id = editingPosition.department_id;
      }
      
      await positionService.update(editingPosition.id, updateData);
      
      if (onDataChange) {
        await onDataChange();
      }
      
      closeEditModal();
      showToast('Должность успешно обновлена', 'success');
    } catch (err) {
      showToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [editingPosition, onDataChange, showToast, closeEditModal]);

  const openDeleteModal = useCallback((position) => {
    setPositionToDelete(position);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setPositionToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!positionToDelete) return;
    
    setLoading(true);
    try {
      const { positionService } = await import('../../../services/positionService');
      await positionService.delete(positionToDelete.id);
      
      if (onDataChange) {
        await onDataChange();
      }
      
      showToast(`Должность "${positionToDelete.name}" успешно удалена`, 'success');
      closeDeleteModal();
    } catch (err) {
      showToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [positionToDelete, onDataChange, showToast, closeDeleteModal]);

  // Получение всех уникальных подразделений
  const allDepartments = useMemo(() => {
    const deptMap = new Map();
    
    if (departments && Array.isArray(departments)) {
      departments.forEach(dept => {
        if (!deptMap.has(dept.id)) {
          deptMap.set(dept.id, {
            id: dept.id,
            name: dept.name,
            positions: []
          });
        }
      });
    }
    
    positions.forEach(position => {
      if (position.departments && Array.isArray(position.departments)) {
        position.departments.forEach(dept => {
          if (!deptMap.has(dept.id)) {
            deptMap.set(dept.id, {
              id: dept.id,
              name: dept.name,
              positions: []
            });
          }
        });
      }
    });
    
    return Array.from(deptMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [positions, departments]);

  // Фильтрация должностей
  const filteredPositions = useMemo(() => {
    if (!searchPosition.trim()) return positions;
    const searchLower = searchPosition.toLowerCase();
    return positions.filter(pos => {
      if (pos.name.toLowerCase().includes(searchLower)) return true;
      if (pos.departments && Array.isArray(pos.departments)) {
        return pos.departments.some(dept => 
          dept.name.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  }, [positions, searchPosition]);

  // Группировка должностей по подразделениям
  const groupedByDepartment = useMemo(() => {
    const groups = {};
    
    allDepartments.forEach(dept => {
      groups[dept.id] = {
        department: dept,
        positions: []
      };
    });
    
    groups['no-department'] = {
      department: { id: 'no-department', name: 'Без подразделения' },
      positions: []
    };
    
    filteredPositions.forEach(position => {
      if (position.departments && position.departments.length > 0) {
        position.departments.forEach(dept => {
          if (groups[dept.id]) {
            groups[dept.id].positions.push({
              ...position,
              currentDepartment: dept
            });
          }
        });
      } else {
        groups['no-department'].positions.push(position);
      }
    });
    
    Object.keys(groups).forEach(key => {
      if (groups[key].positions.length === 0 && key !== 'no-department') {
        delete groups[key];
      }
    });
    
    return groups;
  }, [filteredPositions, allDepartments]);

  const getDepartmentTotalEmployees = useCallback((departmentPositions) => {
    return departmentPositions.reduce((sum, pos) => {
      if (pos.employees_by_department && Array.isArray(pos.employees_by_department)) {
        const deptData = pos.employees_by_department.find(
          e => e.department_id === pos.currentDepartment?.id
        );
        return sum + (deptData?.count || 0);
      }
      return sum + (pos.employees_count || 0);
    }, 0);
  }, []);

  if (isLoading && positions.length === 0) {
    return (
      <div className="directory-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка должностей...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="directory-container">
      {/* Toast уведомления */}
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">
            {toast.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}

      <div className="directory-header">
        <h2>Справочник должностей</h2>
        <button 
          className="btn-primary" 
          onClick={() => setIsModalOpen(true)} 
          disabled={loading || isLoading}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Добавить должность
        </button>
      </div>

      <div className="filters-bar">
        <input 
          type="text" 
          placeholder="Поиск по названию должности или подразделения..." 
          className="search-input" 
          value={searchPosition} 
          onChange={(e) => setSearchPosition(e.target.value)} 
        />
        <button 
          className="btn-secondary" 
          onClick={() => onDataChange?.()} 
          disabled={loading || isLoading}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
            <path d="M21 3v6h-6" />
          </svg>
          Обновить
        </button>
      </div>

      {filteredPositions.length === 0 ? (
        <EmptyState 
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          } 
          title="Должности не найдены" 
          subtitle="Попробуйте изменить поиск или добавьте новую должность" 
        />
      ) : (
        <div className="positions-table-wrapper">
          <table className="directory-table positions-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Должность</th>
                <th style={{ width: '120px', textAlign: 'center' }}>Сотрудников</th>
                <th style={{ width: '100px', textAlign: 'center' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedByDepartment).map(([deptId, group]) => {
                const isExpanded = expandedDepartments[deptId] ?? false;
                const deptTotalEmployees = getDepartmentTotalEmployees(group.positions);
                
                if (group.positions.length === 0 && !searchPosition) return null;
                
                return (
                  <React.Fragment key={deptId}>
                    <tr 
                      className="category-row"
                      onClick={() => toggleDepartment(deptId)}
                    >
                      <td className="category-expand">
                        <span className="expand-icon">
                          {isExpanded ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          )}
                        </span>
                      </td>
                      <td colSpan="3">
                        <strong>{group.department.name}</strong>
                        {!searchPosition && (
                          <span className="category-count">
                            ({group.positions.length} должностей, {deptTotalEmployees} сотр.)
                          </span>
                        )}
                      </td>
                    </tr>
                    
                    {isExpanded && group.positions.map(position => {
                      return (
                        <tr key={`${position.id}-${position.currentDepartment?.id || 'no-dept'}`} className="position-row">
                          <td></td>
                          <td className="position-name">
                            <span className="position-name-text">{position.name}</span>
                          </td>
                          <td className="position-employees" style={{ textAlign: 'center' }}>
                            <span className="employee-count-badge">
                              {position.currentDepartment && position.employees_by_department ? (
                                position.employees_by_department.find(
                                  e => e.department_id === position.currentDepartment.id
                                )?.count || 0
                              ) : (
                                position.employees_count || 0
                              )}
                            </span>
                          </td>
                          <td className="position-actions" style={{ textAlign: 'center' }}>
                            <div className="action-buttons">
                              <button 
                                className="icon-btn small" 
                                onClick={() => openEditModal(position)}
                                disabled={loading}
                                title="Редактировать"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                                  <path d="M3 21h18" />
                                </svg>
                              </button>
                              <button 
                                className="icon-btn small delete" 
                                onClick={() => openDeleteModal(position)}
                                disabled={loading}
                                title="Удалить"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0h10" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Модальное окно для добавления должности - без выпадающего списка категории */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавление новой должности</h3>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Название должности *</label>
                <input 
                  type="text" 
                  placeholder="Введите название должности" 
                  value={newPosition.name} 
                  onChange={(e) => setNewPosition({ ...newPosition, name: e.target.value })} 
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && addPosition()}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsModalOpen(false)}>
                Отмена
              </button>
              <button className="btn-primary" onClick={addPosition} disabled={loading}>
                {loading ? 'Добавление...' : 'Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для редактирования должности */}
      {isEditModalOpen && editingPosition && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Редактирование должности</h3>
              <button className="modal-close" onClick={closeEditModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Название должности *</label>
                <input 
                  type="text" 
                  placeholder="Введите название должности" 
                  value={editingPosition.name} 
                  onChange={(e) => setEditingPosition({ ...editingPosition, name: e.target.value })} 
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && updatePosition()}
                />
              </div>
              <div className="form-group">
                <label>Подразделение</label>
                <select 
                  value={editingPosition.department_id || ''} 
                  onChange={(e) => setEditingPosition({ ...editingPosition, department_id: e.target.value ? parseInt(e.target.value) : '' })}
                >
                  <option value="">Без подразделения</option>
                  {allDepartments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeEditModal}>
                Отмена
              </button>
              <button className="btn-primary" onClick={updatePosition} disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {isDeleteModalOpen && positionToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h3>Подтверждение удаления</h3>
              <button className="modal-close" onClick={closeDeleteModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body delete-body">
              <div className="delete-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>Вы уверены, что хотите удалить должность?</p>
              <p className="delete-position-name">"{positionToDelete.name}"</p>
              {positionToDelete.employees_count > 0 && (
                <p className="delete-warning">
                  Внимание! На этой должности числится {positionToDelete.employees_count} сотрудников.
                  Удаление должности может повлиять на их данные.
                </p>
              )}
              {positionToDelete.departments && positionToDelete.departments.length > 1 && (
                <p className="delete-info">
                  Должность привязана к {positionToDelete.departments.length} подразделениям.
                  Все связи будут удалены.
                </p>
              )}
            </div>
            <div className="modal-footer delete-footer">
              <button className="btn-secondary" onClick={closeDeleteModal}>
                Отмена
              </button>
              <button className="btn-danger" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionsTab;